import React, { useState, useEffect } from 'react';
import { FileText, Download, Loader2, Bell } from 'lucide-react';
import api from '../../utils/axios';
import getFullUrl from '../../utils/formatUrl';

const NoticeBoard = () => {
    const [notices, setNotices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('B.ED');
    const tabs = ['B.ED', 'M.ED', 'Other Program', 'General'];

    useEffect(() => {
        const fetchNotices = async () => {
            try {
                const res = await api.get('/notices/active');
                setNotices(res.data.data);
            } catch (err) {
                console.error("Failed to fetch notices", err);
            } finally {
                setLoading(false);
            }
        };
        fetchNotices();
    }, []);

    const filteredNotices = notices.filter(n => n.program === activeTab);

    return (
        <section className="py-24 bg-white relative overflow-hidden">
            <div className="max-w-4xl mx-auto px-4 relative">
                {/* Header Section */}
                <div className="bg-slate-900 rounded-t-3xl p-6 md:p-8 pb-12 shadow-2xl relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-[#cc0000]/10 rounded-full -translate-y-16 translate-x-16 blur-3xl"></div>
                    
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 relative z-10">
                        <div>
                            <div className="flex items-center space-x-3 mb-4">
                                <div className="bg-[#cc0000]/20 p-2 rounded-lg">
                                    <Bell size={18} className="text-[#cc0000] animate-bounce" />
                                </div>
                                <span className="text-white/60 font-black uppercase text-[10px] tracking-[4px]">Institutional Bulletins</span>
                            </div>
                            <h2 className="text-3xl md:text-4xl font-black text-white uppercase tracking-tighter italic">
                                Notice <span className="text-amber-400">Board.</span>
                            </h2>
                        </div>
                    </div>
                </div>

                {/* Tab Navigation - Hovering over header */}
                <div className="max-w-3xl mx-auto -mt-8 relative z-20">
                    <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-1.5 flex flex-wrap gap-1 shadow-2xl">
                        {tabs.map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`flex-1 min-w-[70px] py-2 md:py-3 rounded-xl text-[8px] md:text-[10px] font-black uppercase tracking-widest transition-all duration-300 ${
                                    activeTab === tab 
                                        ? 'bg-amber-400 text-[#003366] shadow-lg' 
                                        : 'text-white/80 hover:bg-white/10'
                                }`}
                            >
                                {tab}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Notice List */}
                <div className="mt-8 bg-white border border-slate-100 rounded-3xl shadow-[0_40px_80px_-20px_rgba(0,0,0,0.1)] min-h-[400px] flex flex-col">
                    {loading ? (
                        <div className="flex-1 flex flex-col items-center justify-center p-20">
                            <Loader2 size={40} className="animate-spin text-amber-500 mb-4" />
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Hydrating Bulletins...</p>
                        </div>
                    ) : filteredNotices.length === 0 ? (
                        <div className="flex-1 flex flex-col items-center justify-center p-20 text-center">
                            <div className="bg-slate-50 p-6 rounded-full mb-6">
                                <FileText size={48} className="text-slate-200" />
                            </div>
                            <h3 className="text-lg font-black text-[#003366] uppercase">No Notices Found</h3>
                            <p className="text-slate-400 text-xs font-bold mt-2 uppercase tracking-tight">No active bulletins for {activeTab} at this moment.</p>
                        </div>
                    ) : (
                        <div className="p-4 md:p-8">
                            <ul className="space-y-4">
                                {filteredNotices.map((notice, idx) => (
                                    <li key={idx} className="group list-none">
                                        <a 
                                            href={getFullUrl(notice.pdfUrl)} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="flex items-start justify-between p-4 md:p-5 bg-slate-50/50 hover:bg-[#003366] rounded-2xl border border-slate-100 hover:border-[#003366] transition-all duration-500 group/link"
                                        >
                                            <div className="flex items-start gap-5">
                                                <div className="mt-1 flex-shrink-0 w-2 h-2 rounded-full bg-[#cc0000] group-hover:bg-amber-400 transition-colors"></div>
                                                <div>
                                                    <h4 className="text-sm md:text-base font-black text-[#003366] uppercase tracking-tight leading-tight group-hover:text-white transition-colors">
                                                        {notice.title}
                                                    </h4>
                                                    <div className="flex items-center gap-4 mt-2">
                                                        <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest group-hover:text-white/60 transition-colors">
                                                            {new Date(notice.date).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
                                                        </span>
                                                        <span className="text-[8px] font-black bg-slate-200 text-[#003366] px-2 py-0.5 rounded-full uppercase tracking-tighter group-hover:bg-white/20 group-hover:text-amber-400 transition-all">
                                                            Institutional PDF
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            <div className="flex-shrink-0 ml-4 flex items-center justify-center w-12 h-12 rounded-xl bg-white border border-slate-100 group-hover:bg-amber-400 group-hover:border-amber-400 transition-all duration-500 shadow-sm group-hover:shadow-amber-400/50">
                                                <Download size={20} className="text-[#cc0000] group-hover:text-[#003366] group-hover:scale-110 transition-all" />
                                            </div>
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    
                </div>
            </div>
        </section>
    );
};

export default NoticeBoard;
